import React, { useEffect } from 'react'
import './assets/scss/themes.scss'
import { initFirebaseBackend } from './helpers/firebase'
import { connectSocket, setupSocketReconnectListeners } from './helpers/socket'
import { useUser } from './redux/user'
import { saveAuthUser } from './redux/user/actions'
import { dark, light } from '@/styles/theme'
import styled, { ThemeProvider } from 'styled-components'
import Routes from './routes'
import { useThemee } from './redux/theme'
import { changeThemeAction } from './redux/theme/actions'
import { NuqsAdapter } from 'nuqs/adapters/react'
import { GlobalStyle } from '@/styles/global'
import { useCapacitor } from '@/hooks/useCapacitor'
import { ToastContainer } from 'react-toastify'
import { BaseModalBackground, ModalProvider as ModalProviderBase } from 'styled-react-modal'
import { VoiceCallProvider } from '@/features/voice/VoiceCallContext'
import { ImpersonationControl } from '@/features/impersonation/ImpersonationControl'
import { OperatorAppearanceProvider } from '@/features/operators/useOperatorAppearance'
import { BulkCloseJobNotifier } from '@/features/bulkClose/BulkCloseJobNotifier'

const ModalBackground = styled(BaseModalBackground)`
  background-color: rgba(8, 15, 20, 0.4);
  backdrop-filter: blur(8px) saturate(120%);
  -webkit-backdrop-filter: blur(8px) saturate(120%);
`

const ModalProvider = ModalProviderBase as unknown as React.FC<{
  children: React.ReactNode
  backgroundComponent?: React.ComponentType<unknown>
}>

initFirebaseBackend()

function App() {
  const { userDispatch } = useUser()
  const { themeState, themeDispatch } = useThemee()

  const theme = themeState.theme === 'dark' ? dark : light

  useCapacitor({ theme: themeState.theme === 'dark' ? 'dark' : 'light' })

  useEffect(() => {
    const userAuthenticated = localStorage.getItem('devchat_app_userAuthenticated')
    if (userAuthenticated) {
      const parsedAuthUser = JSON.parse(userAuthenticated)
      userDispatch(
        saveAuthUser({
          email: parsedAuthUser.email,
          name: parsedAuthUser.name,
          companies: parsedAuthUser.companies,
          attendCampaigns: parsedAuthUser?.attendCampaigns || [],
          id: parsedAuthUser.id,
          ownerId: parsedAuthUser.ownerId,
          isAdmin: parsedAuthUser?.isAdmin || false,
          departments: parsedAuthUser?.departments || [],
          isWork: parsedAuthUser.isWork,
          role: parsedAuthUser?.role || null,
          token: parsedAuthUser?.token || '',
          accessToken: parsedAuthUser?.accessToken || ''
        })
      )
      connectSocket()
      return setupSocketReconnectListeners()
    }
  }, [userDispatch])

  useEffect(() => {
    const storedTheme = localStorage.getItem('devchat_app_theme')
    if (storedTheme) {
      const parsedTheme = JSON.parse(storedTheme)
      themeDispatch(changeThemeAction({ theme: parsedTheme }))
    }
  }, [])

  return (
    <ThemeProvider theme={theme}>
      <ModalProvider backgroundComponent={ModalBackground as unknown as React.ComponentType<unknown>}>
        <GlobalStyle />
        <NuqsAdapter>
          <VoiceCallProvider>
            <OperatorAppearanceProvider>
              <ImpersonationControl />
              <Routes />
              <BulkCloseJobNotifier />
            </OperatorAppearanceProvider>
          </VoiceCallProvider>
        </NuqsAdapter>
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme={themeState.theme === 'dark' ? 'dark' : 'light'}
        />
      </ModalProvider>
    </ThemeProvider>
  )
}

export default App
